import {
  collectMediaIds,
  isPosterKind,
  modelSlugOf,
  panoAt,
  type FurnitureAssigned,
  type Lesson,
  type MediaInfo,
  type ModelCatalogue,
  type PanoVariants,
  type ServedWorld,
} from '@vrlearning/lesson-schema';
import type { Assignment } from '../api/session-client.js';
import { mediaCacheKey } from '../media-cache.js';
import type { PanoCaps } from '../render/pano-caps.js';
import { panoSource } from '../render/pano-source.js';

/**
 * What a headset fetches ahead of a lesson, in the order it needs it (ADR-038, proposed;
 * docs/superpowers/specs/2026-09-30-lesson-prefetch-design.md, "Main as of 1 October"): the world's
 * files first, then step by step the 360° photo, the posters, the voice-over, the villa's furniture
 * at the furnish step, then anything else the step names. A class that starts at 60 % has the next
 * several steps in, not a random 60 %. Pure: the runner does the fetching.
 *
 * A 360° photo is fetched as the headset will show it (ADR-041, render/pano-source.ts): on a GPU
 * with ASTC a ready photo's light copy and then its cube, never its JPEG; elsewhere the JPEG.
 */

export type PrefetchKind = 'world' | 'pano' | 'poster' | 'voice' | 'furniture' | 'model' | 'other';

export type PrefetchItem = {
  /** Where the service worker keeps it: the world file's URL, or the media path without its query. */
  key: string;
  /** What to fetch: the world file, or the signed media URL as the session gave it. */
  url: string;
  /** The media id, for a media item: a refreshed signature is looked up by it. */
  id?: string;
  /** A 360° photo's variant (ADR-041): its signature is refreshed from `variants`, not `media`. */
  variant?: 'cube' | 'light';
  bytes?: number;
  sha256?: string;
  /** 0 for the world's files, else the lesson step (1-based) that first needs it. */
  step: number;
  kind: PrefetchKind;
};

/** A world file as the manifest names it: its URL, size and the hash it is stored under. */
export type WorldFile = { url: string; bytes?: number; sha256?: string };

export type PlanInput = {
  lesson: Lesson;
  /** Media id -> signed URL: an id without one is not fetched (the lesson evicts it). */
  media: Readonly<Record<string, string>>;
  mediaInfo: MediaInfo;
  /** Media id -> a ready 360° photo's signed cube and light copy (ADR-041). */
  variants: Readonly<PanoVariants>;
  /** What this headset's GPU takes: decides a photo's file. Without a renderer, NO_PANO_CAPS. */
  caps: PanoCaps;
  /** A villa lesson's meubelset (ADR-037): its pieces' GLBs are fetched at the furnish step. */
  furniture?: FurnitureAssigned;
  /** The library models the lesson names (ADR-043): their GLBs and sound takes, where first seen. */
  models?: ModelCatalogue;
  /** The lesson's world files, from the manifest; none for a lesson without a world. */
  worldFiles: readonly WorldFile[];
  /**
   * A served world package's own files (ADR-049), by media id: fetched and kept as media -- in the
   * media cache, under the signed path, re-signed by id -- never as the code worlds' world files.
   */
  worldMedia?: readonly { id: string; bytes: number; sha256: string }[];
  origin: string;
};

export function prefetchPlan(input: PlanInput): PrefetchItem[] {
  const { lesson, media, mediaInfo, origin } = input;
  const items: PrefetchItem[] = [];
  const keys = new Set<string>();
  const ids = new Set<string>();
  /** 360° photos planned as their cube: their JPEG may still be another object's (a poster). */
  const cubes = new Set<string>();

  for (const file of input.worldFiles) {
    const key = new URL(file.url, origin).href;
    if (keys.has(key)) continue;
    keys.add(key);
    items.push({
      key,
      url: file.url,
      ...(file.bytes === undefined ? {} : { bytes: file.bytes }),
      ...(file.sha256 === undefined ? {} : { sha256: file.sha256 }),
      step: 0,
      kind: 'world',
    });
  }

  const push = (item: PrefetchItem): void => {
    if (keys.has(item.key)) return;
    keys.add(item.key);
    items.push(item);
  };

  /** A 360° photo: its cube's light copy and cube, or its JPEG (then as any media). */
  const addPano = (id: string | undefined, step: number): void => {
    if (id === undefined || cubes.has(id)) return;
    const source = panoSource(id, media, input.variants, input.caps);
    if (source?.kind !== 'cube') {
      add(id, step, 'pano');
      return;
    }
    cubes.add(id);
    // The light first: the photo's light, ground and sun are made from it (render/pano.ts).
    for (const variant of ['light', 'cube'] as const) {
      const { url, bytes, sha256 } = source[variant];
      push({
        key: mediaCacheKey(url, origin),
        url,
        id,
        variant,
        bytes,
        sha256,
        step,
        kind: 'pano',
      });
    }
  };

  const add = (id: string | undefined, step: number, kind: PrefetchKind): void => {
    if (id === undefined || ids.has(id)) return;
    // The scene's and the steps' photos as the lesson names them (`other`) are the cube planned;
    // a poster or a model showing the same photo still needs its JPEG.
    if (kind === 'other' && cubes.has(id)) return;
    const url = media[id];
    // Not signed: the lesson lets go of it (evictUnsigned), so it is not fetched either.
    if (url === undefined) return;
    ids.add(id);
    const key = mediaCacheKey(url, origin);
    if (keys.has(key)) return;
    keys.add(key);
    const facts = mediaInfo[id];
    items.push({
      key,
      url,
      id,
      ...(facts ? { bytes: facts.bytes, sha256: facts.sha256 } : {}),
      step,
      kind,
    });
  };

  const objects = Object.entries(lesson.objects ?? {});
  /** The step index an object is first seen at: 0, or a hidden one's first `show`. */
  const seenAt = (id: string, hidden: boolean | undefined): number => {
    if (!hidden) return 0;
    const index = lesson.steps.findIndex((step) =>
      (step.show as readonly string[] | undefined)?.includes(id),
    );
    return index === -1 ? lesson.steps.length - 1 : index;
  };

  // A package's files come first, as the world's do (step 0), with the sizes and hashes it was served with.
  for (const file of input.worldMedia ?? []) {
    const url = media[file.id];
    if (url === undefined || ids.has(file.id)) continue;
    ids.add(file.id);
    push({
      key: mediaCacheKey(url, origin),
      url,
      id: file.id,
      bytes: file.bytes,
      sha256: file.sha256,
      step: 0,
      kind: 'other',
    });
  }

  lesson.steps.forEach((step, index) => {
    const number = index + 1;
    addPano(panoAt(lesson, index)?.photo.media, number);
    for (const [id, object] of objects) {
      if (isPosterKind(object.kind) && seenAt(id, object.hidden) === index) {
        add(object.media, number, 'poster');
      }
    }
    add(step.voiceover, number, 'voice');
    if (step.task?.type === 'furnish') {
      for (const piece of input.furniture?.pieces ?? []) add(piece.media, number, 'furniture');
    }
    if (index === 0) {
      if (lesson.scene.type === 'gltf') add(lesson.scene.media, number, 'model');
      // Scene-level ids belong to step 1: whatever the lesson names outside its steps and objects.
      for (const found of collectMediaIds({ ...lesson, steps: [], objects: {} })) {
        add(found, number, 'other');
      }
    }
    // Models: an object's media where it is first seen.
    for (const [id, object] of objects) {
      if (!isPosterKind(object.kind) && seenAt(id, object.hidden) === index) {
        for (const found of collectMediaIds(object)) add(found, number, 'model');
        // A library model's GLB and sound takes are in no lesson field: the catalogue names them.
        const slug = modelSlugOf(object.kind);
        const facts = slug === undefined ? undefined : input.models?.[slug];
        if (facts) {
          add(facts.media, number, 'model');
          for (const sound of facts.sounds)
            for (const take of sound.media) add(take, number, 'model');
        }
      }
    }
    for (const found of collectMediaIds(step)) add(found, number, 'other');
  });

  return items;
}

/**
 * The plan for a lesson as the session gave it (`session.prepare` or `session.assigned`): its media,
 * their facts and its 360° photos' variants, with this headset's world files, origin and GPU caps.
 */
export function assignmentPlan(
  assignment: Pick<
    Assignment,
    'lesson' | 'media' | 'mediaInfo' | 'variants' | 'furniture' | 'models'
  > &
    Partial<Pick<Assignment, 'worlds'>>,
  here: Pick<PlanInput, 'worldFiles' | 'origin' | 'caps'>,
): PrefetchItem[] {
  return prefetchPlan({
    lesson: assignment.lesson,
    media: assignment.media,
    mediaInfo: assignment.mediaInfo,
    variants: assignment.variants,
    ...(assignment.furniture ? { furniture: assignment.furniture } : {}),
    ...(assignment.models ? { models: assignment.models } : {}),
    worldMedia: packageMedia(assignment.lesson, assignment.worlds),
    ...here,
  });
}

/** The files of the served package a world lesson will build, by media id; none for the code worlds. */
export function packageMedia(
  lesson: Pick<Lesson, 'scene'>,
  worlds: Readonly<Record<string, ServedWorld>> | undefined,
): { id: string; bytes: number; sha256: string }[] {
  if (lesson.scene.type !== 'world') return [];
  const served = worlds?.[lesson.scene.world];
  return Object.values(served?.files ?? {}).map((file) => ({
    id: file.mediaId,
    bytes: file.bytes,
    sha256: file.sha256,
  }));
}
